import React from 'react'
import useStyles from './styles'
import { useFormContext } from 'react-hook-form'
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ButtonCta from 'components/atoms/button-cta'
import InfoIcon from 'components/material-symbol/icons/Info'
import InfoAlert from 'components/info-alert'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'

const useEditOptionStyles = makeStyles(theme => ({
  optionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    paddingTop: theme.spacing(1),
  },
  optionLabel: {
    alignItems: 'flex-start',
    margin: 0,
    '& .MuiFormControlLabel-label': {
      marginTop: 2,
      marginLeft: theme.spacing(0.5),
      paddingTop: theme.spacing(0.25),
    },
  },
  optionTitle: {
    fontSize: '1rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
  },
  optionDescription: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginTop: theme.spacing(1),
  },
  radio: {
    padding: theme.spacing(1),
    marginTop: -2,
    color: '#70787C',
    '&.Mui-checked': {
      color: '#0053FF',
    },
  },
}))

const OFFERING_TYPE_OPTIONS = [
  {
    value: 'bookingType',
    title: 'Booking type',
    description:
      'Online or offline payments. Best for one-time lessons. Students book at time of purchase.',
  },
  {
    value: 'lessonPack',
    title: 'Lesson pack',
    description:
      'Online payments required. Best for multi-session coaching. Offer different quantity packages. Students can purchase now and book later.',
  },
]

const SingleOrLessonPack = ({
  isLessonPacks,
  setIsLessonPacks,
  paymentAccounts,
  isEditFlow = false,
  readOnly = false,
}) => {
  const classes = useStyles()
  const editClasses = useEditFormStyles()
  const editOptionClasses = useEditOptionStyles()
  const { setValue } = useFormContext()

  const selectedValue = isLessonPacks ? 'lessonPack' : 'bookingType'

  const handleOfferingTypeChange = event => {
    if (readOnly) return
    const nextIsLessonPacks = event.target.value === 'lessonPack'
    setIsLessonPacks(nextIsLessonPacks)
    setValue('isLessonPacks', nextIsLessonPacks, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  if (isEditFlow) {
    return (
      <Box className={editClasses.fieldGroup}>
        <Typography className={editClasses.sectionHeading}>
          Offering Type
        </Typography>
        <Typography className={editClasses.fieldHelper}>
          {readOnly
            ? 'This offering is configured as one of the types below.'
            : 'Select whether you want to create a single booking type or a pack of multiple lessons.'}
        </Typography>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            className={editOptionClasses.optionsColumn}
            value={selectedValue}
            onChange={handleOfferingTypeChange}
          >
            {OFFERING_TYPE_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                className={editOptionClasses.optionLabel}
                disabled={readOnly}
                control={
                  <Radio
                    className={editOptionClasses.radio}
                    color="primary"
                    data-cy={
                      option.value === 'lessonPack' ? 'lessonPack' : 'single'
                    }
                  />
                }
                label={
                  <Box>
                    <Typography className={editOptionClasses.optionTitle}>
                      {option.title}
                    </Typography>
                    <Typography className={editOptionClasses.optionDescription}>
                      {option.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
        {isLessonPacks ? (
          <InfoAlert mt={2}>
            <Typography variant="body2">
              The ability to sell lesson packs through PGA Coach is offered to
              coaches as a convenience. It is the individual coach&apos;s
              responsibility to establish the terms and conditions of such sales
              and ensure that the sale of lesson packs is compliant with
              applicable law.
            </Typography>
          </InfoAlert>
        ) : null}
        {isLessonPacks && paymentAccounts.length === 0 ? (
          <Box
            mt={2}
            display="flex"
            alignItems="center"
            className={classes.infoWrapper}
          >
            <Box className={classes.infoIconWrapper}>
              <InfoIcon />
            </Box>
            <Box className={classes.informationContainer}>
              <Typography variant="body2">
                Heads up! Offering lesson packs requires you have an online
                payment account set up. Get set up with online payments now.
              </Typography>
              <ButtonCta
                variant="text"
                arrow
                href="/account/payments/payment-accounts"
                className={classes.ctaButton}
              >
                Add a Payment Account
              </ButtonCta>
            </Box>
          </Box>
        ) : null}
      </Box>
    )
  }

  return (
    <Box mb={3}>
      <Typography variant="body2" gutterBottom color="textSecondary">
        Select whether you want to create a single booking type or pack of
        multiple lessons.
      </Typography>
      <FormControl>
        <RadioGroup aria-labelledby="Lesson Type">
          <FormControlLabel
            checked={!isLessonPacks}
            control={<Radio color="primary" />}
            label={
              <Box ml={2}>
                <Typography variant="subtitle1">Booking Type</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Online or Offline Payments
                  <br />
                  Best for One-Time Lesson
                  <br />
                  Students Book at Time of Purchase
                </Typography>
              </Box>
            }
            data-cy="single"
            onClick={() => {
              setIsLessonPacks(false)
              setValue('isLessonPacks', false, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
          <FormControlLabel
            checked={isLessonPacks}
            control={<Radio color="primary" />}
            label={
              <Box ml={2}>
                <Typography variant="subtitle1">Lesson Pack</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Online Payments Required
                  <br />
                  Best for Multi-Session Coaching
                  <br />
                  Offer Different Quantity Packages
                  <br />
                  Students can Purchase Now, Book Later
                </Typography>
              </Box>
            }
            data-cy="lessonPack"
            onClick={() => {
              setIsLessonPacks(true)
              setValue('isLessonPacks', true, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
        </RadioGroup>
      </FormControl>
      {isLessonPacks ? (
        <Box className={classes.infoWrapper}>
          <Typography variant="body2">
            The ability to sell Lesson Packs through PGA Coach is offered to
            coaches as a convenience. It is the individual coach&apos;s
            responsibility to establish the terms and conditions of such sales
            and ensure that the sale of Lesson Packs is compliant with
            applicable law.
          </Typography>
        </Box>
      ) : null}
      {isLessonPacks && paymentAccounts.length === 0 ? (
        <Box
          mb={3}
          display="flex"
          alignItems="center"
          className={classes.infoWrapper}
        >
          <Box className={classes.infoIconWrapper}>
            <InfoIcon />
          </Box>
          <Box className={classes.informationContainer}>
            <Typography variant="body2">
              Heads up! Offering Lesson Packs requires you have an online
              payment account set up. Get set up with online payments now.
            </Typography>
            <ButtonCta
              variant="text"
              arrow
              href="/account/payments/payment-accounts"
              className={classes.ctaButton}
            >
              Add a Payment Account
            </ButtonCta>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

export default SingleOrLessonPack
